import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { PLAYBOOK_SLUG_BY_ROLE } from "@/data/playbooks";
import ExitIntentPopup from "@/components/ExitIntentPopup";
import {
  Loader2,
  ArrowRight,
  Download,
  Calendar,
  CheckCircle2,
  AlertCircle,
  TrendingUp,
  Shield,
  ExternalLink,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const REVIEW_CALL_URL = "https://calendar.app.google/i1x5bUKAX7oPD9Mu8";

const ROLES = [
  {
    id: "engineering",
    title: "Engineering & Architecture",
    subtitle: "CTO, VP Engineering, Solutions Architect",
    description:
      "Optimize your AI infrastructure, reduce token costs, and implement intelligent routing across models.",
    painPoints: [
      "Managing multi-model complexity",
      "Unpredictable API costs",
      "Poor observability",
    ],
    deliverable: "Token Efficiency Teardown",
    playbookItems: [
      { title: "Token Usage Analysis", description: "Detailed breakdown of your current token consumption patterns" },
      { title: "Model Routing Optimization", description: "Strategic recommendations for intelligent model selection" },
      { title: "Cost Reduction Roadmap", description: "Prioritized actions with projected 30-40% cost savings" },
      { title: "Implementation Timeline", description: "90-day phased rollout plan with quick wins" },
    ],
  },
  {
    id: "finance",
    title: "Finance & Operations",
    subtitle: "CFO, VP Finance, Head of Operations",
    description:
      "Audit tool sprawl, consolidate vendors, and establish ROI frameworks for AI investments.",
    painPoints: [
      "Redundant AI subscriptions",
      "No usage visibility",
      "Budget overruns",
    ],
    deliverable: "AI Vendor Audit",
    playbookItems: [
      { title: "Tool Sprawl Analysis", description: "Complete inventory of AI subscriptions and overlap assessment" },
      { title: "Vendor Consolidation Plan", description: "Recommendations to eliminate redundant tools" },
      { title: "ROI Framework", description: "Measurement system for AI investment returns" },
      { title: "Budget Optimization", description: "Cost-benefit projections with implementation priorities" },
    ],
  },
  {
    id: "sales",
    title: "Sales & Revenue",
    subtitle: "CRO, VP Sales, RevOps Leader",
    description:
      "Deploy AI-powered personalization, automate qualification, and scale outbound without headcount.",
    painPoints: [
      "Poor lead signals",
      "Manual qualification",
      "Generic outreach",
    ],
    deliverable: "Signal-To-Close Pipeline",
    playbookItems: [
      { title: "Signal Detection Strategy", description: "Framework for identifying and acting on buying signals" },
      { title: "Qualification Automation", description: "AI-powered MEDDIC extraction from conversations" },
      { title: "Pipeline Intelligence", description: "Forecast accuracy improvements with AI analysis" },
      { title: "Implementation Roadmap", description: "Phased rollout with minimal rep training required" },
    ],
  },
];

const QUESTIONS = {
  engineering: [
    { id: "q1", question: "How are you routing LLM queries?", type: "select", options: [
        { value: "", label: "Select your approach..." },
        { value: "default-gpt4", label: "Default to GPT-4o" },
        { value: "manual-failovers", label: "Manual failovers" },
        { value: "semantic-router", label: "Semantic router" },
        { value: "other", label: "Other (please specify)" },
      ], drillDown: { triggerValues: ["manual-failovers", "semantic-router", "other"], question: "Tell us more about your setup.", type: "text", placeholder: "e.g., cost-based routing, custom orchestrator, hybrid stack..." } },
    { id: "q2", question: "Are you utilizing prompt context caching for large payloads?", type: "select", options: [
        { value: "", label: "Select..." },
        { value: "yes-extensively", label: "Yes, extensively" },
        { value: "yes-limited", label: "Yes, in limited cases" },
        { value: "no-planning", label: "No, but planning to" },
        { value: "no-unaware", label: "No, wasn't aware of this" },
      ] },
    { id: "q3", question: "How confident are you in your internal vector search / RAG pipelines in production?", type: "select", options: [
        { value: "", label: "Select confidence level..." },
        { value: "very-confident", label: "Very confident - battle-tested" },
        { value: "somewhat", label: "Somewhat confident" },
        { value: "not-confident", label: "Not confident - needs work" },
        { value: "no-rag", label: "Not using RAG yet" },
      ] },
    { id: "q4", question: "How do you track token usage and API burn rate in real-time?", type: "text", placeholder: "Describe your monitoring setup..." },
    { id: "q5", question: "Are you locked into a single frontier model vendor or are you model-agnostic?", type: "select", options: [
        { value: "", label: "Select..." },
        { value: "single-vendor", label: "Single vendor (e.g., all OpenAI)" },
        { value: "two-vendors", label: "Two vendors" },
        { value: "model-agnostic", label: "Fully model-agnostic" },
      ] },
    { id: "q6", question: "How do you test and evaluate LLM output before it hits production?", type: "text", placeholder: "Describe your testing process..." },
    { id: "q7", question: "Do you have a centralized gateway for AI API access, or do individual teams manage their own keys?", type: "select", options: [
        { value: "", label: "Select..." },
        { value: "centralized", label: "Centralized gateway" },
        { value: "semi-centralized", label: "Partially centralized" },
        { value: "decentralized", label: "Teams manage their own keys" },
      ] },
    { id: "q8", question: "Security: How do you handle SOC II compliance and data privacy when sending data to LLM APIs?", type: "text", placeholder: "Describe your compliance approach..." },
    { id: "q9", question: "Training: Do your AI vendors or internal AI tools require fine-tuning or training on your proprietary data?", type: "select", options: [
        { value: "", label: "Select..." },
        { value: "heavy-training", label: "Yes, requires heavy training" },
        { value: "minimal-training", label: "Minimal training" },
        { value: "zero-training", label: "Zero-training / No-training required" },
      ] },
    { id: "q10", question: "What is your approximate monthly API burn rate for AI models?", type: "select", options: [
        { value: "", label: "Select range..." },
        { value: "under-1k", label: "Under $1,000" },
        { value: "1k-5k", label: "$1,000 - $5,000" },
        { value: "5k-25k", label: "$5,000 - $25,000" },
        { value: "25k-100k", label: "$25,000 - $100,000" },
        { value: "over-100k", label: "Over $100,000" },
      ] },
    { id: "q11", question: "What is your timeline for optimizing these operations?", type: "select", options: [
        { value: "", label: "Select timeline..." },
        { value: "asap", label: "ASAP" },
        { value: "next-3-months", label: "Next 3 months" },
        { value: "next-6-months", label: "Next 6 months" },
        { value: "dont-know", label: "Don't know / Just taking in data" },
      ] },
  ],
  finance: [
    { id: "q1", question: 'How many specialized "AI-enabled" software subscriptions is your team currently paying for?', type: "select", options: [
        { value: "", label: "Select range..." },
        { value: "1-2", label: "1-2 tools" },
        { value: "3-5", label: "3-5 tools" },
        { value: "6-10", label: "6-10 tools" },
        { value: "11-20", label: "11-20 tools" },
        { value: "over-20", label: "Over 20 tools" },
      ], drillDown: { triggerValues: ["3-5", "6-10", "11-20", "over-20"], question: "What is the estimated total monthly spend on these fragmented tools?", type: "select", options: [
          { value: "", label: "Select monthly range..." },
          { value: "0-5000", label: "$0-$5,000" },
          { value: "5000-15000", label: "$5,000-$15,000" },
          { value: "15000-25000", label: "$15,000-$25,000" },
          { value: "25000-plus", label: "$25,000 or greater" },
        ] } },
    { id: "q2", question: "What is the most manual, time-consuming data task in your operations right now?", type: "text", placeholder: "Describe the task..." },
    { id: "q3", question: "How long does it take for the executive team to interrogate operational data?", type: "select", options: [
        { value: "", label: "Select timeframe..." },
        { value: "real-time", label: "Real-time / On-demand" },
        { value: "few-hours", label: "A few hours" },
        { value: "day", label: "1 business day" },
        { value: "days", label: "2-3 business days" },
        { value: "week-plus", label: "A week or more" },
      ] },
    { id: "q4", question: "Have you attempted to consolidate AI features into internal tools to save on SaaS premiums?", type: "select", options: [
        { value: "", label: "Select..." },
        { value: "yes-successfully", label: "Yes, successfully" },
        { value: "yes-failed", label: "Yes, but it failed" },
        { value: "considering", label: "Considering it" },
        { value: "no", label: "No" },
      ] },
    { id: "q5", question: "How do you measure the ROI of your AI software investments?", type: "text", placeholder: "Describe your measurement approach..." },
    { id: "q6", question: "Who owns the purchasing decision for department-level AI tools?", type: "select", options: [
        { value: "", label: "Select..." },
        { value: "centralized-it", label: "Centralized (IT/Finance)" },
        { value: "dept-heads", label: "Department heads" },
        { value: "individual-teams", label: "Individual teams/managers" },
        { value: "mixed", label: "Mixed approach" },
      ] },
    { id: "q7", question: "How are you auditing the data privacy policies of the AI tools your employees adopt?", type: "text", placeholder: "Describe your audit process..." },
    { id: "q8", question: "Security: Do you require SOC II compliance from your AI vendors?", type: "select", options: [
        { value: "", label: "Select..." },
        { value: "yes-required", label: "Yes, required" },
        { value: "preferred", label: "Preferred but not required" },
        { value: "no", label: "No" },
      ] },
    { id: "q9", question: 'Training: Do the AI tools you purchase require extensive employee training, or do they offer "no-training required" onboarding?', type: "select", options: [
        { value: "", label: "Select..." },
        { value: "extensive", label: "Extensive training required" },
        { value: "moderate", label: "Moderate training (1-2 sessions)" },
        { value: "minimal", label: "Minimal training" },
        { value: "no-training", label: "No-training required" },
      ] },
    { id: "q10", question: "Does your company currently have a dedicated internal software engineering team?", type: "select", options: [
        { value: "", label: "Select..." },
        { value: "yes-large", label: "Yes, large team (10+ engineers)" },
        { value: "yes-small", label: "Yes, small team (1-10 engineers)" },
        { value: "contractors", label: "Contractors only" },
        { value: "no", label: "No internal engineering" },
      ] },
    { id: "q11", question: "What is your timeline for optimizing these operations?", type: "select", options: [
        { value: "", label: "Select timeline..." },
        { value: "asap", label: "ASAP" },
        { value: "next-3-months", label: "Next 3 months" },
        { value: "next-6-months", label: "Next 6 months" },
        { value: "dont-know", label: "Don't know / Just taking in data" },
      ] },
  ],
  sales: [
    { id: "q1", question: "How much time do your Account Executives spend researching prospects before a call?", type: "select", options: [
        { value: "", label: "Select timeframe..." },
        { value: "under-5", label: "Under 5 minutes" },
        { value: "5-15", label: "5-15 minutes" },
        { value: "15-30", label: "15-30 minutes" },
        { value: "30-60", label: "30-60 minutes" },
        { value: "over-60", label: "Over 60 minutes" },
      ], drillDown: { triggerValues: ["15-30", "30-60", "over-60"], question: "What data sources are they manually checking?", type: "text", placeholder: "e.g., LinkedIn, company website, Crunchbase..." } },
    { id: "q2", question: "How accurate and up-to-date is your CRM data after sales calls?", type: "select", options: [
        { value: "", label: "Select..." },
        { value: "very-accurate", label: "Very accurate - automatically updated" },
        { value: "mostly-accurate", label: "Mostly accurate - some manual updates" },
        { value: "inconsistent", label: "Inconsistent - depends on rep" },
        { value: "outdated", label: "Often outdated or incomplete" },
      ] },
    { id: "q3", question: "How does your team react to external buying signals (e.g., a prospect raising funding)?", type: "select", options: [
        { value: "", label: "Select..." },
        { value: "automated-alerts", label: "Automated alerts trigger outreach" },
        { value: "manual-monitoring", label: "Manual monitoring and reaction" },
        { value: "occasional", label: "Occasionally catch signals" },
        { value: "reactive", label: "Mostly reactive" },
      ] },
    { id: "q4", question: "How many different sales intelligence and sequencing tools does your team use?", type: "select", options: [
        { value: "", label: "Select..." },
        { value: "1", label: "1 tool" },
        { value: "2-3", label: "2-3 tools" },
        { value: "4-6", label: "4-6 tools" },
        { value: "over-6", label: "Over 6 tools" },
      ] },
    { id: "q5", question: "Do you have automated systems extracting MEDDIC criteria directly from call transcripts?", type: "select", options: [
        { value: "", label: "Select..." },
        { value: "yes-automated", label: "Yes, fully automated" },
        { value: "partial", label: "Partially automated" },
        { value: "manual", label: "Manual extraction" },
        { value: "no-transcripts", label: "No call transcription" },
      ] },
    { id: "q6", question: "Are your forecasts based on rep sentiment or AI-driven analysis of call transcripts?", type: "select", options: [
        { value: "", label: "Select..." },
        { value: "ai-driven", label: "AI-driven analysis" },
        { value: "hybrid", label: "Hybrid (AI + rep input)" },
        { value: "rep-sentiment", label: "Primarily rep sentiment" },
        { value: "gut-feel", label: "Gut feel / intuition" },
      ] },
    { id: "q7", question: "Are reps using unauthorized AI tools (like personal ChatGPT accounts) to draft emails?", type: "select", options: [
        { value: "", label: "Select..." },
        { value: "yes-widespread", label: "Yes, widespread" },
        { value: "some-reps", label: "Some reps" },
        { value: "no", label: "No" },
        { value: "unknown", label: "Unknown / unsure" },
      ] },
    { id: "q8", question: "Security: Do your enterprise clients require your tech stack (including AI tools) to be SOC II compliant?", type: "select", options: [
        { value: "", label: "Select..." },
        { value: "yes-required", label: "Yes, required by most clients" },
        { value: "sometimes", label: "Sometimes" },
        { value: "no", label: "No" },
      ] },
    { id: "q9", question: 'Training: Do the AI sales tools you use require reps to learn new workflows, or do they operate invisibly (no-training required)?', type: "select", options: [
        { value: "", label: "Select..." },
        { value: "extensive-training", label: "Extensive new workflows" },
        { value: "moderate", label: "Moderate training required" },
        { value: "minimal", label: "Minimal training" },
        { value: "invisible", label: "Invisible / no-training required" },
      ] },
    { id: "q10", question: "What CRM system is your revenue team currently anchored to?", type: "select", options: [
        { value: "", label: "Select CRM..." },
        { value: "salesforce", label: "Salesforce" },
        { value: "hubspot", label: "HubSpot" },
        { value: "pipedrive", label: "Pipedrive" },
        { value: "close", label: "Close" },
        { value: "other", label: "Other CRM" },
        { value: "none", label: "No CRM" },
      ] },
    { id: "q11", question: "What is your timeline for optimizing these operations?", type: "select", options: [
        { value: "", label: "Select timeline..." },
        { value: "asap", label: "ASAP" },
        { value: "next-3-months", label: "Next 3 months" },
        { value: "next-6-months", label: "Next 6 months" },
        { value: "dont-know", label: "Don't know / Just taking in data" },
      ] },
  ],
};

export default function Parse() {
  const [step, setStep] = useState(1);
  const [selectedRole, setSelectedRole] = useState("");
  const [answers, setAnswers] = useState({});
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  // Force scroll to top on every funnel state change — role select, question
  // advance/retreat, analysis loader, email gate, and thank-you. The global
  // ScrollToTop component handles route-level navigation; this useEffect
  // handles intra-page funnel transitions where the URL doesn't change.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [step, currentQuestionIndex]);

  // Browser back button handler — when user is on step 2+, intercept the
  // back navigation and return to step 1 instead of leaving the page.
  // We push a history entry on the Step 1 → Step 2 transition (in
  // handleRoleSelect) so popstate fires reliably for downstream steps too.
  useEffect(() => {
    const handler = () => {
      if (step > 1) {
        setStep(1);
        setCurrentQuestionIndex(0);
        // Re-push so subsequent back presses also return here, not the
        // referring page (e.g. /, /services). Cleared on natural step-1
        // navigation away.
        window.history.pushState({ parseStep: 1 }, "", window.location.pathname);
      }
    };
    window.addEventListener("popstate", handler);
    return () => window.removeEventListener("popstate", handler);
  }, [step]);

  const generateDynamicFindings = () => {
    const findings = [];
    if (selectedRole === "engineering") {
      if (answers.q1 === "default-gpt4") findings.push("Single-model dependency detected - diversifying to cost-effective alternatives could reduce spend by 35-45%");
      else if (answers.q1 === "manual-failovers") findings.push("Manual routing introduces latency and operational overhead - semantic routing could automate this");
      if (answers.q2 === "no-unaware" || answers.q2 === "no-planning") findings.push("Prompt caching not implemented - immediate 50-70% cost reduction opportunity for repeated contexts");
      if (answers.q4 && (answers.q4.toLowerCase().includes("no") || answers.q4.toLowerCase().includes("basic"))) findings.push("Limited observability creates blind spots - real-time cost tracking essential for optimization");
      if (answers.q10 === "25k-100k" || answers.q10 === "over-100k") findings.push("High API spend detected - our analysis shows $8,400-$15,000/year in immediate savings available");
    }
    if (selectedRole === "finance") {
      if (answers.q1 === "6-10" || answers.q1 === "11-20" || answers.q1 === "over-20") findings.push("Significant tool sprawl identified - consolidation could eliminate 40-60% of redundant subscriptions");
      if (answers.q3 === "days" || answers.q3 === "week-plus") findings.push("Slow data access is costing executive productivity - automation can reduce to real-time");
      if (answers.q5 && (answers.q5.toLowerCase().includes("don't") || answers.q5.toLowerCase().includes("no"))) findings.push("No ROI tracking in place - establishing measurement framework is critical for budget optimization");
      if (answers.q1_drilldown) {
        const spendLabels = {
          "0-5000": "$0–$5,000",
          "5000-15000": "$5,000–$15,000",
          "15000-25000": "$15,000–$25,000",
          "25000-plus": "$25,000+",
        };
        const label = spendLabels[answers.q1_drilldown];
        if (label) findings.push(`Monthly AI spend of ${label} identified - vendor consolidation typically saves 30-45%`);
      }
    }
    if (selectedRole === "sales") {
      if (answers.q1 === "30-60" || answers.q1 === "over-60") findings.push("Excessive manual research time - AI-powered prospect intelligence could reduce this by 80%");
      if (answers.q2 === "inconsistent" || answers.q2 === "outdated") findings.push("Poor CRM data quality impacts forecast accuracy - automated enrichment would improve by 60%");
      if (answers.q5 === "manual" || answers.q5 === "no-transcripts") findings.push("Manual MEDDIC qualification is time-intensive - AI extraction from calls can automate this entirely");
      if (answers.q4 === "4-6" || answers.q4 === "over-6") findings.push("Multiple disconnected tools create data silos - unified platform could eliminate 3-4 subscriptions");
    }
    if (findings.length === 0) {
      findings.push("Multiple optimization opportunities identified across your current setup");
      findings.push("Quick wins available with 1-2 week implementation time");
    } else if (findings.length === 1) {
      findings.push("Additional optimization opportunities identified in vendor selection and workflow automation");
    }
    return findings.slice(0, 3);
  };

  const handleRoleSelect = (roleId) => {
    setSelectedRole(roleId);
    setCurrentQuestionIndex(0);
    setAnswers({});
    setStep(2);
    // Push a synthetic history entry so the browser back button returns to
    // Step 1 of the funnel rather than leaving /parse altogether.
    window.history.pushState({ parseStep: 2 }, "", window.location.pathname);
  };

  const handleAnswer = (questionId, value) => {
    setAnswers({ ...answers, [questionId]: value });
  };

  const handleNextQuestion = () => {
    const currentQuestions = QUESTIONS[selectedRole];
    const currentQ = currentQuestions[currentQuestionIndex];
    if (currentQ.drillDown && currentQ.drillDown.triggerValues.includes(answers[currentQ.id])) {
      const drillDownId = `${currentQ.id}_drilldown`;
      if (!answers[drillDownId]) return;
    }
    if (currentQuestionIndex < currentQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      setStep(3);
      setTimeout(() => setStep(4), 2500);
    }
  };

  const handlePrevQuestion = () => {
    if (currentQuestionIndex > 0) setCurrentQuestionIndex(currentQuestionIndex - 1);
    else setStep(1);
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (email && name) {
      try {
        await fetch(`${BACKEND_URL}/api/parse/leads`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name, email, role: selectedRole, answers, score: 68 }),
        });
      } catch (_err) {
        /* Save is best-effort — user still advances to the results step. */
      }
      setStep(5);
    }
  };

  const currentQuestions = selectedRole ? QUESTIONS[selectedRole] : [];
  const currentQ = currentQuestions[currentQuestionIndex];
  const isCurrentQuestionAnswered = currentQ && answers[currentQ.id] && answers[currentQ.id].trim() !== "";
  const needsDrillDown = currentQ?.drillDown && isCurrentQuestionAnswered && currentQ.drillDown.triggerValues.includes(answers[currentQ.id]);
  const drillDownAnswered = needsDrillDown ? (answers[`${currentQ.id}_drilldown`] && answers[`${currentQ.id}_drilldown`].trim() !== "") : true;
  const canProceed = isCurrentQuestionAnswered && drillDownAnswered;
  const getRoleData = () => ROLES.find((r) => r.id === selectedRole) || ROLES[0];
  const dynamicFindings = step >= 4 ? generateDynamicFindings() : [];

  return (
    <div className="bg-[#0a1628] text-white" data-testid="parse-page">
      <Helmet>
        <title>Parse · Free AI Stack Assessment — Reduce SaaS Bloat with Internal AI</title>
        <meta name="description" content="Free 10-minute AI stack assessment from SideB. Diagnose where your AI operations are leaking margin, identify SaaS bloat, and get a vendor-agnostic optimization playbook. AI Solutions Management insights, no email upsell." />
        <meta name="keywords" content="AI Solutions Management, AIOps Partner, AI stack assessment, Reduce SaaS bloat with internal AI, AI-first RevOps consulting, Process Automation Consulting" />
        <meta property="og:title" content="Parse · Free AI Stack Assessment — Reduce SaaS Bloat with Internal AI" />
        <meta property="og:description" content="10-minute AI stack diagnostic. Identify SaaS bloat, cost leaks, and optimization opportunities — with a tailored playbook from SideB's AI Solutions Management team." />
        <meta property="og:url" content="https://sideb.io/parse" />
        <meta name="twitter:title" content="Parse · Free AI Stack Assessment" />
        <meta name="twitter:description" content="10-minute diagnostic. Reduce SaaS bloat with internal AI. Tailored playbook from SideB." />
      </Helmet>

      <div className="container mx-auto px-6 py-16 max-w-6xl">
        {/* Step 1 */}
        {step === 1 && (
          <div className="animate-in fade-in duration-500">
            <div className="text-center mb-20 max-w-4xl mx-auto">
              <div className="inline-block mb-8">
                <span className="text-gray-400 uppercase text-xs tracking-[0.25em] font-medium">SIDEB · PARSE ASSESSMENT</span>
              </div>
              <h1 className="text-5xl md:text-6xl font-normal mb-8 tracking-tight leading-[1.1]">
                Diagnose your AI stack.<br />Get your optimization playbook.
              </h1>
              <p className="text-xl text-gray-400 leading-relaxed mb-12">
                A 10-minute assessment that maps your current AI operations, identifies cost leaks and inefficiencies, and delivers a custom playbook with prioritized recommendations.
              </p>

              <div className="bg-gradient-to-br from-[#0f1e33] to-[#0a1628] border border-[#1e3a5f] rounded-3xl p-10 mb-8 shadow-[0_0_50px_rgba(185,103,255,0.08)]">
                <h3 className="text-xl font-medium mb-10 text-center text-[#b967ff] tracking-wide">What you&rsquo;ll receive</h3>
                <div className="grid md:grid-cols-3 gap-8 text-left">
                  {[
                    { title: "Structural Optimization Score", body: "Based on architectural efficiency patterns and public optimization data" },
                    { title: "Vendor-Agnostic Blueprint", body: "Role-specific configuration recommendations independent of vendor lock-in" },
                    { title: "Prioritized Quick Wins", body: "Immediate optimization actions ranked by impact and ease" },
                  ].map((item) => (
                    <div key={item.title} className="flex flex-col items-start gap-4 p-6 bg-[#0a1628]/50 rounded-2xl border border-[#1e3a5f]/50 hover:border-[#b967ff]/30 transition-all">
                      <div className="bg-[#b967ff]/10 p-3 rounded-xl">
                        <CheckCircle2 className="w-6 h-6 text-[#b967ff]" />
                      </div>
                      <div>
                        <p className="font-semibold text-base mb-2 text-white">{item.title}</p>
                        <p className="text-sm text-gray-400 leading-relaxed">{item.body}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-center">
                <Link to="/security" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-[#b967ff] transition-colors group">
                  <Shield className="w-4 h-4" />
                  <span>100% Confidential &middot; SOC II Certified</span>
                  <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-normal mb-4 text-center text-white">Select your domain</h2>
              <p className="text-center text-gray-400 mb-10">Choose the area where you lead operations</p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {ROLES.map((role) => (
                <Card
                  key={role.id}
                  className="bg-[#0f1e33] border-[#1e3a5f] hover:border-[#b967ff] transition-all duration-300 cursor-pointer group hover:shadow-[0_0_40px_rgba(185,103,255,0.15)]"
                  data-testid={`parse-role-${role.id}`}
                >
                  <CardContent className="p-8" onClick={() => handleRoleSelect(role.id)}>
                    <h3 className="text-xl font-medium mb-2 text-white group-hover:text-[#b967ff] transition-colors">{role.title}</h3>
                    <p className="text-sm text-gray-400 mb-4">{role.subtitle}</p>
                    <p className="text-sm text-gray-300 leading-relaxed mb-6">{role.description}</p>
                    <div className="space-y-2 mb-6">
                      <p className="text-xs uppercase tracking-wider text-gray-400 font-medium">Common pain points</p>
                      {role.painPoints.map((pain) => (
                        <div key={pain} className="flex items-start gap-2">
                          <AlertCircle className="w-3.5 h-3.5 text-gray-500 flex-shrink-0 mt-0.5" />
                          <p className="text-xs text-gray-400">{pain}</p>
                        </div>
                      ))}
                    </div>
                    <div className="pt-4 border-t border-[#1e3a5f]">
                      <p className="text-xs uppercase tracking-wider text-gray-400 font-medium mb-2">Deliverable</p>
                      <p className="text-sm text-[#b967ff]">{role.deliverable}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Step 2 */}
        {step === 2 && currentQ && (
          <div className="animate-in fade-in duration-300 max-w-3xl mx-auto">
            <div className="mb-12">
              <span className="text-gray-400 uppercase text-xs tracking-[0.25em] font-medium">
                QUESTION {currentQuestionIndex + 1} OF {currentQuestions.length}
              </span>
              <div className="w-full bg-[#1e3a5f] h-2 rounded-full mt-4 mb-8">
                <div className="bg-[#b967ff] h-2 rounded-full transition-all duration-500" style={{ width: `${((currentQuestionIndex + 1) / currentQuestions.length) * 100}%` }} />
              </div>
              <h2 className="text-3xl font-normal mt-4 mb-2 text-white">{getRoleData().title}</h2>
            </div>

            <div className="space-y-8">
              <div className="space-y-4">
                <label className="block">
                  <p className="text-2xl font-normal mb-6 text-white">{currentQ.question}</p>
                  {currentQ.type === "text" && (
                    <Input
                      type="text"
                      value={answers[currentQ.id] || ""}
                      onChange={(e) => handleAnswer(currentQ.id, e.target.value)}
                      placeholder={currentQ.placeholder}
                      className="bg-[#0f1e33] border-[#1e3a5f] h-14 text-base text-white placeholder:text-gray-500 focus:border-[#b967ff] focus:ring-[#b967ff] rounded-xl"
                      data-testid="parse-answer-text"
                    />
                  )}
                  {currentQ.type === "select" && (
                    <select
                      value={answers[currentQ.id] || ""}
                      onChange={(e) => handleAnswer(currentQ.id, e.target.value)}
                      className="w-full bg-[#0f1e33] border border-[#1e3a5f] h-14 text-base text-white rounded-xl px-4 focus:border-[#b967ff] focus:ring-[#b967ff] focus:outline-none transition-colors"
                      data-testid="parse-answer-select"
                    >
                      {currentQ.options.map((option) => (
                        <option key={option.value} value={option.value} className="bg-[#0a1628]">{option.label}</option>
                      ))}
                    </select>
                  )}
                </label>
              </div>

              {needsDrillDown && (
                <div className="space-y-4 pl-6 border-l-2 border-[#b967ff] animate-in fade-in duration-300">
                  <label className="block">
                    <p className="text-lg font-normal mb-4 text-[#b967ff]">{currentQ.drillDown.question}</p>
                    {currentQ.drillDown.type === "select" ? (
                      <select
                        value={answers[`${currentQ.id}_drilldown`] || ""}
                        onChange={(e) => handleAnswer(`${currentQ.id}_drilldown`, e.target.value)}
                        className="w-full bg-[#0f1e33] border border-[#1e3a5f] h-14 text-base text-white rounded-xl px-4 focus:border-[#b967ff] focus:ring-[#b967ff] focus:outline-none transition-colors"
                        data-testid="parse-drilldown"
                      >
                        {currentQ.drillDown.options.map((option) => (
                          <option key={option.value} value={option.value} className="bg-[#0a1628]">{option.label}</option>
                        ))}
                      </select>
                    ) : (
                      <Input
                        type="text"
                        value={answers[`${currentQ.id}_drilldown`] || ""}
                        onChange={(e) => handleAnswer(`${currentQ.id}_drilldown`, e.target.value)}
                        placeholder={currentQ.drillDown.placeholder}
                        className="bg-[#0f1e33] border-[#1e3a5f] h-14 text-base text-white placeholder:text-gray-500 focus:border-[#b967ff] focus:ring-[#b967ff] rounded-xl"
                        data-testid="parse-drilldown"
                      />
                    )}
                  </label>
                </div>
              )}
            </div>

            <div className="mt-12 flex justify-between items-center">
              <Button onClick={handlePrevQuestion} variant="ghost" className="text-gray-400 hover:text-white hover:bg-[#0f1e33]" data-testid="parse-prev-btn">
                &larr; {currentQuestionIndex === 0 ? "Back to roles" : "Previous"}
              </Button>
              <Button onClick={handleNextQuestion} disabled={!canProceed} className="bg-[#b967ff] text-white hover:bg-[#a855f7] px-10 py-6 text-base font-medium rounded-full disabled:opacity-30 disabled:cursor-not-allowed transition-all" data-testid="parse-next-btn">
                {currentQuestionIndex === currentQuestions.length - 1 ? "Analyze My Stack" : "Next"}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3 — Loading */}
        {step === 3 && (
          <div className="animate-in fade-in duration-500 flex flex-col items-center justify-center py-24 max-w-2xl mx-auto text-center">
            <Loader2 className="h-20 w-20 text-[#b967ff] animate-spin mb-8" />
            <p className="text-2xl font-normal text-gray-200 mb-4">
              Analyzing your {getRoleData().title.toLowerCase()} setup...
            </p>
            <p className="text-base text-gray-400">Applying structural optimization logic and scoring your responses</p>
          </div>
        )}

        {/* Step 4 — Email gate */}
        {step === 4 && (
          <div className="animate-in fade-in duration-500 max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-block mb-8">
                <div className="bg-[#b967ff]/10 border border-[#b967ff]/30 rounded-full px-6 py-2">
                  <span className="text-[#b967ff] uppercase text-xs tracking-[0.25em] font-medium">ANALYSIS COMPLETE</span>
                </div>
              </div>
              <h2 className="text-4xl md:text-5xl font-normal mb-6 leading-tight text-white">
                Your {getRoleData().title.toLowerCase()} shows <span className="text-[#b967ff]">high optimization potential</span>
              </h2>
            </div>

            <div className="space-y-6 mb-12">
              <Card className="bg-[#0f1e33] border-[#1e3a5f]">
                <CardContent className="p-8">
                  <div className="flex items-start gap-4">
                    <div className="bg-[#b967ff]/10 rounded-full p-3">
                      <TrendingUp className="w-6 h-6 text-[#b967ff]" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-medium mb-3 text-white">Key Findings Preview</h3>
                      <div className="space-y-3">
                        {dynamicFindings.map((finding) => (
                          <div key={finding} className="flex items-start gap-3">
                            <CheckCircle2 className="w-5 h-5 text-[#b967ff] flex-shrink-0 mt-0.5" />
                            <p className="text-gray-300">{finding}</p>
                          </div>
                        ))}
                      </div>
                      <div className="mt-6 pt-6 border-t border-[#1e3a5f]">
                        <p className="text-sm text-gray-400">Full analysis includes vendor comparison matrix, 90-day implementation roadmap, and cost-benefit projections</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-[#b967ff]/10 to-[#7c3aed]/10 border-[#b967ff]/30">
                <CardContent className="p-8 text-center">
                  <p className="text-sm uppercase tracking-wider text-gray-400 font-medium mb-4">Your Optimization Score</p>
                  <div className="text-6xl font-normal mb-4">
                    <span className="text-[#b967ff]">68</span>
                    <span className="text-gray-600">/100</span>
                  </div>
                  <p className="text-gray-300">Based on structural optimization patterns. The full report shows specific improvement areas and priority actions.</p>
                </CardContent>
              </Card>
            </div>

            <Card className="bg-[#0f1e33] border-[#b967ff]/50 border-2">
              <CardContent className="p-10">
                <h3 className="text-2xl font-normal mb-6 text-center text-white">Get your full {getRoleData().deliverable}</h3>
                <p className="text-center text-gray-300 mb-8">Enter your details to unlock the complete analysis, prioritized recommendations, and implementation roadmap</p>

                <form onSubmit={handleEmailSubmit} className="space-y-5 max-w-md mx-auto">
                  <div>
                    <label className="block text-sm text-gray-300 mb-2">Name</label>
                    <Input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" required className="bg-white/10 border-[#1e3a5f] h-14 text-base text-white placeholder:text-gray-400 focus:border-[#b967ff] focus:ring-[#b967ff] rounded-xl" data-testid="parse-name-input" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-2">Work Email</label>
                    <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" required className="bg-white/10 border-[#1e3a5f] h-14 text-base text-white placeholder:text-gray-400 focus:border-[#b967ff] focus:ring-[#b967ff] rounded-xl" data-testid="parse-email-input" />
                  </div>
                  <Button type="submit" className="w-full bg-[#b967ff] text-white hover:bg-[#a855f7] py-7 text-base font-medium rounded-full transition-all" data-testid="parse-submit-btn">
                    Download Complete Playbook
                    <Download className="ml-2 h-5 w-5" />
                  </Button>
                </form>

                <div className="text-center mt-6">
                  <p className="text-sm text-gray-400">
                    Or{" "}
                    <a href={REVIEW_CALL_URL} target="_blank" rel="noopener noreferrer" className="text-[#b967ff] hover:underline" data-testid="parse-review-call-link">
                      schedule a 15-min review call
                    </a>{" "}
                    with our team
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 5 — Thank you */}
        {step === 5 && (
          <div className="animate-in fade-in duration-500 max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-normal mb-6 text-white">
                Thank you, <span className="text-[#b967ff]">{name}</span>
              </h2>
              <p className="text-xl text-gray-300 leading-relaxed">
                Your complete {getRoleData().deliverable} is ready.
              </p>
            </div>

            {/* Primary CTA — view the playbook */}
            <Card className="bg-gradient-to-br from-[#b967ff]/15 to-[#7c3aed]/10 border-[#b967ff]/40 border-2 mb-6">
              <CardContent className="p-10 text-center">
                <h3 className="text-2xl font-normal mb-3 text-white">View your full playbook now</h3>
                <p className="text-gray-300 mb-8 leading-relaxed">
                  Online webpage with a downloadable PDF. Walk through the prioritized recommendations and 90-day implementation plan.
                </p>
                <Button asChild className="bg-[#b967ff] text-white hover:bg-[#a855f7] px-10 py-6 text-base font-medium rounded-full" data-testid="parse-view-playbook-btn">
                  <Link to={`/playbooks/${PLAYBOOK_SLUG_BY_ROLE[selectedRole] || ""}`}>
                    <Download className="mr-2 h-5 w-5" />
                    Open {getRoleData().deliverable}
                  </Link>
                </Button>
              </CardContent>
            </Card>

            <div className="space-y-6">
              <Card className="bg-[#0f1e33] border-[#1e3a5f]">
                <CardContent className="p-10">
                  <h3 className="text-2xl font-normal mb-6 text-center text-white">What&rsquo;s included in your playbook</h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    {getRoleData().playbookItems.map((item) => (
                      <div key={item.title} className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-[#b967ff] flex-shrink-0 mt-1" />
                        <div>
                          <p className="font-medium mb-1 text-white">{item.title}</p>
                          <p className="text-sm text-gray-300">{item.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-[#b967ff]/10 to-[#7c3aed]/10 border-[#b967ff]/30">
                <CardContent className="p-10 text-center">
                  <h3 className="text-2xl font-normal mb-4 text-white">Want to discuss your results?</h3>
                  <p className="text-gray-300 mb-8 leading-relaxed">Book a 15-minute review call to walk through your playbook with a sideb.io consultant</p>
                  <Button asChild className="bg-[#b967ff] text-white hover:bg-[#a855f7] px-10 py-6 text-base font-medium rounded-full" data-testid="parse-schedule-btn">
                    <a href={REVIEW_CALL_URL} target="_blank" rel="noopener noreferrer">
                      <Calendar className="mr-2 h-5 w-5" />
                      Schedule Review Call
                    </a>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>

      <ExitIntentPopup
        enabled={step === 1}
        storageKey="sideb:parse-exit-intent-shown"
        eyebrow="Before you go"
        title="Not sure where to start?"
        description="Skip the assessment — book a 15-minute review call with our team and we'll walk through your biggest AI optimization opportunities."
        primaryHref="https://calendar.app.google/i1x5bUKAX7oPD9Mu8"
        primaryLabel="Schedule a call"
        primaryExternal={true}
        chatContext="Exit intent · Parse assessment"
        secondaryLabel="Chat with us instead"
        dismissLabel="No thanks, I'll keep exploring"
      />
    </div>
  );
}
